/**
 * AuthGuard Component
 *
 * Handles the Notice ID + Confirmation Code authentication flow for the DOE settlement.
 * After successful authentication, redirects users to the FPA settlement site.
 *
 * Uses an inline multi-step flow (no modals):
 *   Step 1: Notice ID + Confirmation Code
 *   Step 2: SMS consent + last-4-digits input
 *   Step 3: OTP 6-digit entry
 *
 * @module AuthGuard
 * @since 1.0.0
 */

import { createElement } from "commonAssets/lib/createElement";
import { validateField } from "commonAssets/lib/validateField";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError";
import { validOTP } from "commonAssets/lib/validOTP";
import { loadingGif } from "commonAssets/lib/loadingGif.js";
import {
    loading,
    appConfigDev,
    appConfigKey,
    appConfigProd,
} from "commonAssets/js/constants";

export class AuthGuard {
    /**
     * @param {Object} options - Configuration options
     * @param {Object} options.i18n - Internationalization instance
     * @param {Object} options.errorHandler - Error handler instance
     * @param {string} options.targetDomain - Target domain for FPA settlement (e.g., "www.fpasettlement.com")
     */
    constructor({ i18n, errorHandler, targetDomain }) {
        this.i18n = i18n;
        this.errorHandler = errorHandler;
        this.targetDomain = targetDomain;
        this.api = null;
        this._step1HTML = "";
    }

    /**
     * Initialize the AuthGuard component
     * Sets up the API client and renders the form
     */
    init() {
        this._initApiClient();
        this._renderForm();
        this._bindEvents();

        // Save Step 1 content for Reset
        this._step1HTML = this._getContainer().innerHTML;
    }

    /**
     * Initialize the API client based on environment
     * @private
     */
    _initApiClient() {
        const hostname = window.location.hostname;
        let env = appConfigProd;

        if (
            hostname.startsWith("local") ||
            hostname === "localhost" ||
            hostname.split(".")[1] === "angeiontest"
        ) {
            env = appConfigDev;
        }

        this.api = apigClientFactory.newClient({
            apiKey: appConfigKey,
            url: env,
        });
    }

    /**
     * Create and render the authentication form (Step 1)
     * @private
     */
    _renderForm() {
        const formElement = createElement(
            "fieldset",
            {
                class: "col-md-6 mb-5 offset-md-3 mt-5",
                id: "notice-confirmation",
            },
            [
                createElement(
                    "div",
                    {
                        class: "bg-light text-dark border rounded p-3",
                        id: "auth-content",
                    },
                    [
                        createElement(
                            "p",
                            { class: "mb-3" },
                            [],
                            'If you received a personalized notice in the mail or via email with a <strong class="underline">Notice ID</strong> and <strong class="underline">Confirmation Code</strong>, please enter the codes you were provided below. <br><br> Please remember to enter the full Notice ID exactly as it appears on your personalized Notice, (i.e. 12345678).'
                        ),
                        createElement("div", { class: "mb-3" }, [
                            createElement("input", {
                                type: "text",
                                name: "zero_zero_id",
                                class: "form-control",
                                id: "zero_zero_id",
                                "data-validate": "required",
                                required: "",
                            }),
                            createElement(
                                "label",
                                { for: "zero_zero_id" },
                                [],
                                'Notice ID <span class="error"></span>'
                            ),
                        ]),
                        createElement("div", { class: "mb-3" }, [
                            createElement("input", {
                                type: "text",
                                name: "confirmation_code",
                                class: "form-control",
                                id: "confirmation_code",
                                "data-validate": "required",
                                required: "",
                            }),
                            createElement(
                                "label",
                                { for: "confirmation_code" },
                                [],
                                'Confirmation Code <span class="error"></span>'
                            ),
                        ]),
                        createElement("div", {}, [
                            createElement("input", {
                                type: "submit",
                                class: "btn btn-primary",
                                id: "submit-guard",
                                value: "Submit",
                            }),
                        ]),
                    ]
                ),
            ]
        );

        document.getElementById("output").appendChild(formElement);
    }

    /**
     * Get the auth content container element
     * @returns {HTMLElement}
     * @private
     */
    _getContainer() {
        return document.getElementById("auth-content");
    }

    /**
     * Transition the container content with a fade effect
     * @param {string} html - New HTML content
     * @param {Function} [afterSwap] - Callback to run after content is swapped (for binding events)
     * @private
     */
    _transitionTo(html, afterSwap) {
        const container = this._getContainer();
        container.classList.add("fade-out");

        setTimeout(() => {
            container.innerHTML = html;
            container.classList.remove("fade-out");

            if (afterSwap) afterSwap();
        }, 500);
    }

    /**
     * Show an inline alert within the current step
     * @param {string} message - Alert message (plain text, escaped for safety)
     * @param {Object} [options] - Options
     * @param {string} [options.tryAgainStep] - Step to return to on Try Again ("step1" | "step2")
     * @param {string} [options.email_address] - Email address (needed if tryAgainStep is "step2")
     * @private
     */
    _showInlineAlert(message, options = {}) {
        const alertDiv = document.createElement("div");
        alertDiv.className = "alert alert-danger";
        alertDiv.setAttribute("role", "alert");

        const messagePara = document.createElement("p");
        messagePara.textContent = message;
        alertDiv.appendChild(messagePara);

        if (options.tryAgainStep) {
            const btnWrapper = document.createElement("div");
            btnWrapper.className = "d-flex gap-2";

            const tryAgainBtn = document.createElement("button");
            tryAgainBtn.type = "button";
            tryAgainBtn.className = "btn btn-outline-danger btn-sm";
            tryAgainBtn.textContent = "Try Again";
            tryAgainBtn.addEventListener("click", () => {
                if (options.tryAgainStep === "step2" && options.email_address) {
                    this._showSMSConsentStep(options.email_address);
                } else {
                    this._resetFlow();
                }
            });

            btnWrapper.appendChild(tryAgainBtn);
            alertDiv.appendChild(btnWrapper);
        }

        this._transitionTo("", () => {
            this._getContainer().appendChild(alertDiv);
        });
    }

    /**
     * Reset the flow back to Step 1
     * @private
     */
    _resetFlow() {
        this._transitionTo(this._step1HTML, () => {
            this._bindEvents();
        });
    }

    /**
     * Build a Reset link element (used in Steps 2 and 3)
     * @returns {string} HTML string for the Reset link
     * @private
     */
    _resetLinkHTML() {
        return '<div class="mt-3 text-center"><a href="javascript:void(0)" id="reset-flow" class="text-muted small">Start Over</a></div>';
    }

    /**
     * Bind the Reset link event listener
     * @private
     */
    _bindResetLink() {
        const resetLink = document.getElementById("reset-flow");
        if (resetLink) {
            resetLink.addEventListener("click", () => this._resetFlow());
        }
    }

    /**
     * Bind event listeners for Step 1
     * @private
     */
    _bindEvents() {
        document
            .getElementById("submit-guard")
            .addEventListener("click", (e) => this._handleSubmit(e));
    }

    /**
     * Handle form submission (Step 1)
     * @param {Event} e - Click event
     * @private
     */
    _handleSubmit(e) {
        e.preventDefault();

        const zero_zero_id = document.getElementById("zero_zero_id");
        const confirmation_code = document.getElementById("confirmation_code");

        let valid = true;
        const member = {
            domain: this.targetDomain,
            zero_zero_id: zero_zero_id.value.toUpperCase(),
            confirmation_code: confirmation_code.value,
        };

        // Validate guard fields
        [zero_zero_id, confirmation_code].forEach((input) => {
            valid = validateField(input) && valid;
        });

        if (!valid) return;

        // Store validated credentials for FPA redirect handoff
        this._z = member.zero_zero_id;
        this._c = member.confirmation_code;

        loadingGif(loading, "show");
        this._verifyClassMember(member, zero_zero_id, confirmation_code);
    }

    /**
     * Verify class member via API
     * @param {Object} member - Member data
     * @param {HTMLElement} zero_zero_id - Notice ID input element
     * @param {HTMLElement} confirmation_code - Confirmation code input element
     * @private
     */
    _verifyClassMember(member, zero_zero_id, confirmation_code) {
        this.api
            .classMemberPost({}, member)
            .then((result) => {
                // Check for error message
                if (result.data.error) {
                    toggleFieldError(zero_zero_id, true, "");
                    toggleFieldError(confirmation_code, true, "");
                    loadingGif(loading, "hide", 500);
                    this._showInlineAlert(result.data.error, { tryAgainStep: "step1" });
                    return;
                }

                const cm_data = result.data.class_member || result.data;

                // OTP override — redirect immediately
                if (cm_data.otp_override) {
                    this._redirectToFPA();
                    return;
                }

                // SMS phone number must be present and true
                if (!cm_data.sms_phone_number) {
                    loadingGif(loading, "hide", 500);
                    this._showInlineAlert(
                        "Please contact the Settlement Administrator at 844-314-4124, to verify your identity as a valid Settlement Class Member.",
                        { tryAgainStep: "step1" }
                    );
                    return;
                }

                // email_address is still required by the OTP endpoint
                if (!cm_data.email_address) {
                    loadingGif(loading, "hide", 500);
                    this._showInlineAlert(
                        "Please contact the Settlement Administrator at 844-314-4124, to verify your identity as a valid Settlement Class Member.",
                        { tryAgainStep: "step1" }
                    );
                    return;
                }

                loadingGif(loading, "hide", 500);
                this._showSMSConsentStep(cm_data.email_address);
            })
            .catch((error) => {
                loadingGif(loading, "hide", 500);
                this.errorHandler.log_exception(
                    new Error("Error loading class member", { cause: error })
                );
                this._showInlineAlert(
                    this.i18n.getTranslation("claim_processing_error"),
                    { tryAgainStep: "step1" }
                );
            });
    }

    /**
     * Show SMS consent step inline (Step 2)
     * @param {string} email_address - Email address for OTP request
     * @private
     */
    _showSMSConsentStep(email_address) {
        const stepHTML = [
            '<form id="sms-consent-form">',
            "<h5 class=\"mb-3\">SMS Verification</h5>",
            "<p>To verify your identity, we need to send you a one-time verification code to your phone number on file via text message.</p>",
            '<p>By clicking &quot;Send Verification Code&quot; you agree to receive a one-time SMS verification message. Message and data rates may apply. This is not a marketing message.</p>',
            '<p>If you do not consent to receive a text message, please contact us at 844-314-4124 so our agents can verify your identity through alternative means. <a href="javascript:void(0)" id="call-center-otp-link">Click here for over-the-phone OTP.</a></p>',
            '<p>Please enter the <strong>last 4 digits</strong> of your phone number to confirm:</p>',
            '<div class="mb-3">',
            '<input type="text" id="sms-last-four" class="form-control" ',
            'maxlength="4" minlength="4" pattern="\\d{4}" ',
            'inputmode="numeric" placeholder="Last 4 digits" required />',
            '<div class="invalid-feedback" id="sms-last-four-error" style="display:none;">Please enter exactly 4 numeric digits.</div>',
            "</div>",
            '<button type="submit" class="btn btn-primary" id="submit-sms-consent">Send Verification Code</button>',
            "</form>",
            this._resetLinkHTML(),
        ].join("");

        this._transitionTo(stepHTML, () => {
            this._bindResetLink();

            document.getElementById("call-center-otp-link")
                .addEventListener("click", () => this._showCallCenterOTPStep(email_address));

            const form = document.getElementById("sms-consent-form");
            form.addEventListener("submit", (e) => {
                e.preventDefault();

                const input = document
                    .getElementById("sms-last-four")
                    .value.trim();

                // Validate exactly 4 numeric digits
                if (!/^\d{4}$/.test(input)) {
                    const errorEl = document.getElementById("sms-last-four-error");
                    errorEl.style.display = "block";
                    return;
                }

                loadingGif(loading, "show");
                this._requestOTP(email_address, input);
            });
        });
    }

    /**
     * Show call center OTP step inline (Step 2b)
     * User calls 844-314-4124, receives OTP from agent, enters it here.
     * Skips otpGet — goes directly to otpPost for validation.
     * @param {string} email_address - Email address for OTP validation
     * @private
     */
    _showCallCenterOTPStep(email_address) {
        const stepHTML = [
            '<form id="otp-form" method="post">',
            '<h5 class="mb-3">Over-the-Phone Verification</h5>',
            "<p>Please call the Settlement Administrator at <strong>844-314-4124</strong> to receive a one-time verification code.</p>",
            "<p>Once you have received your code, please enter it below:</p>",
            '<div class="otp-inputs">',
            '<input type="text" />'.repeat(6),
            "</div>",
            '<div id="otp-error" class="text-danger small mt-2" style="display:none;"></div>',
            '<div class="row mt-3 mx-auto col-md-2">',
            '<input type="submit" id="submit-otp" class="btn btn-primary" value="Submit" />',
            "</div>",
            "</form>",
            this._resetLinkHTML(),
        ].join("");

        this._transitionTo(stepHTML, () => {
            this._bindResetLink();
            this._initOTPForm(email_address);
        });
    }

    /**
     * Request OTP for SMS verification
     * @param {string} email_address - Email address for OTP request
     * @param {string} last_4 - Last 4 digits of phone number
     * @private
     */
    _requestOTP(email_address, last_4) {
        const otp_request = {
            authorizationToken: "",
            domain: this.targetDomain,
            email_address: email_address,
            delivery_method: "sms",
            last_4: last_4,
        };

        this.api
            .otpGet(otp_request)
            .then((result) => {
                if (result.status !== 200) {
                    loadingGif(loading, "hide", 250);
                    this._showInlineAlert(
                        this.i18n.getTranslation("claim_processing_error"),
                        { tryAgainStep: "step2", email_address }
                    );
                    return;
                }

                loadingGif(loading, "hide", 250);
                this._showOTPStep(email_address);
            })
            .catch((error) => {
                loadingGif(loading, "hide", 250);
                this._showInlineAlert(
                    this.i18n.getTranslation("claim_processing_error"),
                    { tryAgainStep: "step2", email_address }
                );
                this.errorHandler.log_exception(
                    new Error("Error requesting OTP", { cause: error })
                );
            });
    }

    /**
     * Show OTP entry step inline (Step 3)
     * @param {string} email_address - Email address for OTP verification
     * @private
     */
    _showOTPStep(email_address) {
        const stepHTML = [
            '<form id="otp-form" method="post">',
            `<h5 class="mb-3">${this.i18n.getTranslation("otp_title")}</h5>`,
            `<p>${this.i18n.getTranslation("otp_instructions_1")}<br>${this.i18n.getTranslation("otp_instructions_2")}</p>`,
            '<div class="otp-inputs">',
            '<input type="text" />'.repeat(6),
            "</div>",
            '<div id="otp-error" class="text-danger small mt-2" style="display:none;"></div>',
            '<div class="row mt-3 mx-auto col-md-2">',
            '<input type="submit" id="submit-otp" class="btn btn-primary" value="Submit" />',
            "</div>",
            "</form>",
            this._resetLinkHTML(),
        ].join("");

        this._transitionTo(stepHTML, () => {
            this._bindResetLink();
            this._initOTPForm(email_address);
        });
    }

    /**
     * Initialize OTP form event handling
     * @param {string} email_address - Email address for OTP verification
     * @private
     */
    _initOTPForm(email_address) {
        const otp = new VanillaOTP(".otp-inputs");

        document.getElementById("otp-form").addEventListener("submit", (e) => {
            e.preventDefault();

            const errorEl = document.getElementById("otp-error");
            errorEl.style.display = "none";

            if (validOTP(otp.getValue())) {
                loading.classList.add("show");
                document.getElementById("submit-otp").disabled = true;

                this._validateOTP(email_address, otp.getValue());
            } else {
                errorEl.textContent = this.i18n.getTranslation("otp_validation_error");
                errorEl.style.display = "block";
            }
        });
    }

    /**
     * Validate OTP code via API
     * @param {string} email_address - Email address
     * @param {string} code - OTP code entered by user
     * @private
     */
    _validateOTP(email_address, code) {
        const otp_request = {
            domain: this.targetDomain,
            email_address: email_address,
            code: code,
        };

        this.api
            .otpPost({}, otp_request)
            .then((result) => {
                if (result.status === 200) {
                    this._redirectToFPA();
                } else {
                    loading.classList.remove("show");
                    document.getElementById("submit-otp").disabled = false;
                    const errorEl = document.getElementById("otp-error");
                    errorEl.textContent = this.i18n.getTranslation("otp_validation_error");
                    errorEl.style.display = "block";
                }
            })
            .catch((error) => {
                loading.classList.remove("show");
                document.getElementById("submit-otp").disabled = false;
                const errorEl = document.getElementById("otp-error");
                errorEl.textContent = this.i18n.getTranslation("otp_validation_error");
                errorEl.style.display = "block";
                this.errorHandler.log_exception(
                    new Error("Error validating OTP", { cause: error })
                );
            });
    }

    /**
     * Redirect to the FPA settlement site
     * @private
     */
    _redirectToFPA() {
        const fragment = `z=${encodeURIComponent(this._z)}&c=${encodeURIComponent(this._c)}`;
        window.location.href = `https://${this.targetDomain}#${fragment}`;
    }
}
